import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { resolveApiErrorMessage } from '@/api/resolveApiErrorMessage';
import type { PatientFile } from '@/api/files/filesApi.types';
import { Badge, type BadgeTone } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Icon } from '@/components/common/Icon';
import { FilePreviewModal } from '@/components/common/FilePreviewModal';
import { RelatedRecordsSection } from '@/components/practitioner/shared/RelatedRecordsSection';
import {
  ALLOWED_FILE_UPLOAD_CONTENT_TYPES,
  FILE_UPLOAD_ACCEPT_ATTRIBUTE,
  FILE_VISIBILITY,
  MAXIMUM_FILE_UPLOAD_SIZE_BYTES,
} from '@/constants/files';
import { isAcceptedContentType } from '@/constants/media';
import { formatFileSize } from '@/utils/fileSize';
import { formatIsoDateTimeToTimestamp } from '@/utils/dateFormatting';
import { fetchPatientFileAttachmentUrl } from '@/api/files/filesApi';
import { triggerAttachmentDownload } from '@/utils/fileDownload';
import { showErrorNotification, showSuccessNotification } from '@/utils/notifications';
import {
  usePatientFilesQuery,
  useSetFileVisibilityMutation,
  useUploadPatientFileMutation,
} from '@/hooks/useFilesQueries';
import { ExerciseVideoLabel } from './ExerciseVideoLabel';
import { resolveFileIcon, type FilePreviewTarget } from './fileRowSupport';
import { PendingFileRow } from './PendingFileRow';

interface FilesSectionProps {
  patientId: string;
}

export function FilesSection({ patientId }: FilesSectionProps) {
  const { t } = useTranslation('files');
  const filesQuery = usePatientFilesQuery(patientId);
  const uploadMutation = useUploadPatientFileMutation();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [preview, setPreview] = useState<FilePreviewTarget | null>(null);

  const files = filesQuery.data === undefined ? [] : filesQuery.data;
  const approvedAndRejectedFiles = files.filter(function isReviewed(file) {
    return file.status !== 'pending';
  });
  const pendingFiles = files.filter(function isPending(file) {
    return file.status === 'pending';
  });

  function selectFile() {
    if (fileInputRef.current !== null) {
      fileInputRef.current.click();
    }
  }

  function handleFileSelected(changeEvent: React.ChangeEvent<HTMLInputElement>) {
    const selectedFiles = changeEvent.target.files;
    if (selectedFiles === null) {
      return;
    }
    const selected = selectedFiles.item(0);
    changeEvent.target.value = '';
    if (selected === null) {
      return;
    }
    if (isAcceptedContentType(selected, ALLOWED_FILE_UPLOAD_CONTENT_TYPES) === false) {
      showErrorNotification(t('validation.unsupportedType'));
      return;
    }
    if (selected.size > MAXIMUM_FILE_UPLOAD_SIZE_BYTES) {
      showErrorNotification(t('validation.tooLarge'));
      return;
    }
    uploadMutation.mutate(
      { patientId, file: selected },
      {
        onSuccess: function done() {
          showSuccessNotification(t('toasts.uploaded'));
        },
        onError: function fail(error) {
          const message = resolveApiErrorMessage(error);
          showErrorNotification(message.length > 0 ? message : t('toasts.uploadError'));
        },
      }
    );
  }

  return (
    <>
      <RelatedRecordsSection
        title={t('section.title')}
        isLoading={filesQuery.isLoading}
        isError={filesQuery.isError}
        onRetry={function retry() {
          filesQuery.refetch();
        }}
        actions={
          <>
            <Button
              variant="outline"
              size="chip"
              isLoading={uploadMutation.isPending}
              onClick={selectFile}
            >
              {uploadMutation.isPending ? null : <Icon name="Upload" />}
              {uploadMutation.isPending ? t('section.uploading') : t('section.upload')}
            </Button>
            <input
              ref={fileInputRef}
              type="file"
              accept={FILE_UPLOAD_ACCEPT_ATTRIBUTE}
              className="hidden"
              onChange={handleFileSelected}
            />
          </>
        }
      >
        {pendingFiles.length > 0 ? (
          <div className="mb-4">
            <p className="text-[10px] font-bold uppercase tracking-wider text-zoomuted mb-2">
              {t('section.awaitingReview')}
            </p>
            <div className="space-y-2">
              {pendingFiles.map(function renderPendingFile(file) {
                return (
                  <PendingFileRow
                    key={file.id}
                    file={file}
                    patientId={patientId}
                    onPreview={setPreview}
                  />
                );
              })}
            </div>
          </div>
        ) : null}

        {approvedAndRejectedFiles.length === 0 ? (
          <p className="text-sm text-zoomuted py-2">{t('section.empty')}</p>
        ) : (
          <div className="space-y-2">
            {approvedAndRejectedFiles.map(function renderFile(file) {
              return (
                <FileRow key={file.id} file={file} patientId={patientId} onPreview={setPreview} />
              );
            })}
          </div>
        )}
      </RelatedRecordsSection>

      <FilePreviewModal
        isOpen={preview !== null}
        onClose={function closePreview() {
          setPreview(null);
        }}
        url={preview === null ? '' : preview.url}
        filename={preview === null ? '' : preview.filename}
        contentType={preview === null ? null : preview.contentType}
        resolveDownloadUrl={
          preview === null
            ? undefined
            : function resolvePreviewDownloadUrl() {
                return fetchPatientFileAttachmentUrl(preview.fileId);
              }
        }
      />
    </>
  );
}

function resolveStatusTone(status: string): BadgeTone {
  if (status === 'approved') {
    return 'success';
  }
  if (status === 'rejected') {
    return 'danger';
  }
  return 'neutral';
}

interface FileRowProps {
  file: PatientFile;
  patientId: string;
  onPreview: (target: FilePreviewTarget) => void;
}

function FileRow({ file, patientId, onPreview }: FileRowProps) {
  const { t } = useTranslation('files');
  const visibilityMutation = useSetFileVisibilityMutation();
  const isClientVisible = file.visibility === FILE_VISIBILITY.clientVisible;

  function toggleVisibility() {
    const nextVisibility = isClientVisible
      ? FILE_VISIBILITY.therapistOnly
      : FILE_VISIBILITY.clientVisible;
    visibilityMutation.mutate(
      { fileId: file.id, patientId, visibility: nextVisibility },
      {
        onSuccess: function done() {
          showSuccessNotification(t('toasts.visibilityUpdated'));
        },
        onError: function fail(error) {
          const message = resolveApiErrorMessage(error);
          showErrorNotification(message.length > 0 ? message : t('toasts.visibilityError'));
        },
      }
    );
  }

  async function download() {
    if (file.downloadUrl === null) {
      return;
    }
    try {
      const attachmentUrl = await fetchPatientFileAttachmentUrl(file.id);
      triggerAttachmentDownload(attachmentUrl);
    } catch {
      showErrorNotification(t('toasts.downloadError'));
    }
  }

  function preview() {
    if (file.downloadUrl === null) {
      return;
    }
    onPreview({
      fileId: file.id,
      url: file.downloadUrl,
      filename: file.filename,
      contentType: file.contentType,
    });
  }

  return (
    <div className="flex flex-wrap items-center gap-3 p-3 border border-zooborder rounded-xl">
      {file.thumbnailUrl === null ? (
        <span className="w-9 h-9 rounded-lg bg-zoovpalelavender text-zooprimary flex items-center justify-center shrink-0">
          <Icon name={resolveFileIcon(file.contentType)} />
        </span>
      ) : (
        <img
          src={file.thumbnailUrl}
          alt={file.filename}
          className="w-9 h-9 rounded-lg object-cover shrink-0"
        />
      )}
      <div className="min-w-0 flex-1">
        {file.downloadUrl === null ? (
          <p className="text-sm font-semibold text-zooindigo truncate">{file.filename}</p>
        ) : (
          <button
            type="button"
            onClick={preview}
            className="text-sm font-semibold text-zooindigo truncate hover:text-zooprimary hover:underline text-left"
          >
            {file.filename}
          </button>
        )}
        <div className="flex items-center gap-1.5 flex-wrap mt-1">
          <Badge tone="muted" size="sm">
            {formatFileSize(file.sizeBytes)}
          </Badge>
          <ExerciseVideoLabel patientId={patientId} exerciseId={file.treatmentPlanExerciseId} />
          <Badge tone="neutral" size="sm">
            {t('source.' + file.source)}
          </Badge>
          <Badge tone={resolveStatusTone(file.status)} size="sm">
            {t('status.' + file.status)}
          </Badge>
          <span className="text-[11px] text-zoomuted">
            {formatIsoDateTimeToTimestamp(file.createdAt)}
          </span>
        </div>
      </div>
      <div className="flex items-center gap-1.5 flex-wrap justify-end w-full sm:w-auto sm:shrink-0">
        {file.status === 'approved' ? (
          <Button
            variant="outline"
            size="chip"
            isLoading={visibilityMutation.isPending}
            onClick={toggleVisibility}
          >
            {isClientVisible ? t('visibility.makeTherapistOnly') : t('visibility.makeClientVisible')}
          </Button>
        ) : null}
        {file.status === 'approved' ? (
          <Button
            variant="outline"
            size="chip"
            onClick={function handleDownload() {
              void download();
            }}
          >
            <Icon name="Download" />
            {t('actions.download')}
          </Button>
        ) : null}
      </div>
    </div>
  );
}
